import { Block, Box, MainContainer, Image } from "../../../styles/basicStyles";
import {
  CareerCircle1,
  CareerCircle2,
  ContactCircle1,
  ContactCircle2,
  ProcessCircle1,
  ProcessCircle2,
  QuestioMarkWrapper,
  WorkCircle1
} from "./styles";
import { colors } from "../../../styles/colors";
import { FC } from "react";
import { useTranslation } from "next-i18next";
import TextButton from "../../textButton";
import Typography from "../../typography";
import Icon from "../../icon";
import Dot from "../dot";
import Typewriter from 'typewriter-effect';

interface GlobalBannerProps {
  page?: string;
  logo?: string;
  title?: string;
  description: string;
  bgColor: string;
  questionMark?: boolean;
  dot?: boolean;
  dotColor?: string;
  linkText?: string;
  linkUrl?: () => void;
  blacktext?: boolean;
  origin?: "work" | "career" | "contact" | "blog" | "process";
}

const GlobalBanner: FC<GlobalBannerProps> = ({
  page,
  logo,
  title,
  description,
  bgColor,
  questionMark,
  dot,
  dotColor,
  linkText,
  linkUrl,
  blacktext = false,
  origin,
}) => {
  const { t, i18n: { language } } = useTranslation();
  
  return (
    <Block
      pt={{ md: 10, xxs: 7.5 }}
      pb={{ md: linkText ? 4.25 : 5.688, xxs: 3.75 }}
      bgColor={bgColor}
      position="relative"
      overflow="hidden"
    >
      {
        origin === 'career' ?
        <>
          <CareerCircle1 />
          <CareerCircle2 />
        </>
        : 
        origin === "contact" ?
        <>
          <ContactCircle1 />
          <ContactCircle2 />
        </>
        : 
        origin === "process" ?
        <>
          <ProcessCircle1 />
          <ProcessCircle2 />
        </>
        : 
        (origin === "work" || origin === 'blog') ?
        <WorkCircle1 />
        : 
        null
      }
      <MainContainer>
        <Box 
          fDirection={{ lg: "row", xxs: "column" }} 
          fAlign="flex-end" 
          position='relative' 
          zIndex={2}
        >
          <Box fDirection="column" w={{ lg: '60%', xxs: '100%' }}>
            {logo && <Image src={logo} maxW={9.375} maxH={6.25} alt='logo' />}
            {page && (
              <Typography
                as='div'
                fSize={1.063}
                lHeight={1.25}
                fWeight={400}
                color={blacktext ? colors.chineseBlack : colors.white}
                opacity={0.75}
              >
                {page?.toUpperCase()}
              </Typography>
            )}
            <Typography
              as='h1'
              fSize={{ md: 3.125, xxs: 2.313 }}
              lHeight={{ md: 3.75, xxs: 2.938 }}
              fWeight={700}
              color={blacktext ? colors.black : colors.white}
              mt={0.688}
            >
              {
                origin === 'career' ?
                <>
                  <div>
                    {t('DO')}&nbsp;
                    <Typewriter
                      options={{
                        strings: [t('MEANINGFUL'), t('AMAZING'), t('VALUABLE'), t('AMBICIOUS'), t('EXCITING')],
                        autoStart: true,
                        loop: true
                      }}
                    />
                    {language === 'en' && ` ${t("WORK").toLowerCase()}`}
                  </div>
                  <div>
                    {t('IN_INSPIRING_TEAM')}
                    {dot &&
                      <Dot
                        w={{ md: 0.875, xxs: 0.5 }}
                        h={{ md: 0.875, xxs: 0.5 }}
                        ml={0.375}
                        bgColor={dotColor}
                      />
                    }
                  </div>
                </>
                :
                <>
                  {title ? title : ''}
                  {questionMark && (
                    <QuestioMarkWrapper>
                      <Icon noColor icon="outlined_questionMark" />
                    </QuestioMarkWrapper>
                  )}
                  {dot && (
                    <Dot
                      w={{ md: 0.875, xxs: 0.5 }}
                      h={{ md: 0.875, xxs: 0.5 }}
                      ml={0.375}
                      bgColor={dotColor}
                    />
                  )}
                </>
              }
            </Typography>
          </Box>
          <Box fJustify={{ lg: "flex-end", xxs: 'flex-start' }} w={{ lg: '40%', xxs: '100%' }}>
            <Typography
              fSize={{ md: 1.063, xxs: 1.063 }}
              lHeight={{ md: 1.688, xxs: 1.875 }}
              fWeight={400}
              color={blacktext ? colors.black : colors.white}
              mt={{ lg: 0, xxs: 1.5 }}
              maxW={{ lg: 27.75, xxs: "100%" }}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </Box>
        </Box>
        {linkText && (
          <Block display="flex">
            <TextButton
              primaryColor={colors.lightBlue}
              hoveredColor={colors.white}
              text={linkText}
              icon="outlined_arrowRight"
              mt={{ md: 3.688, xxs: 2.625 }}
              onClick={linkUrl}
            />
          </Block>
        )}
      </MainContainer>
    </Block>
  );
};

export default GlobalBanner;
